import React, { useState, useEffect } from 'react';
import { MainNav } from "@/components/MainNav";
import { DealersListView } from "@/components/dealers/DealersListView";
import { DealersFilter } from "@/components/dealers/DealersFilter";
import { DealersCityHeader } from "@/components/dealers/DealersCityHeader";
import { DealersCitySEO } from "@/components/dealers/DealersCitySEO";
import { DealersMap } from "@/components/DealersMap";
import { useHydratedDealersByCity } from "@/hooks/dealers/useHydratedDealersByCity";
import { useDealersByCityWithInfiniteScroll } from "@/hooks/dealers/useDealersByCityWithInfiniteScroll";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { useSEOStats } from "@/hooks/dealers/useSEOStats";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { List, Map } from "lucide-react";
import { isSSR } from "@/lib/isSSR";
import { ServerDealersCityContent } from "@/components/dealers/ServerDealersCityContent";
import { Dealer } from "@/types/dealer";
import { Skeleton } from "@/components/ui/skeleton";

interface DealersByCityProps {
  serverDealers?: Dealer[];
  serverFormattedCity?: string;
}

const DealersByCity = () => {
  // Stato per forzare loading iniziale e prevenire flash di errore
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Feature flag per infinite scroll - può essere controllato da env o config
  const [enableInfiniteScroll] = useState(true);
  
  // Hook originale come fallback
  const originalData = useHydratedDealersByCity();
  
  // Nuovo hook con infinite scroll
  const infiniteScrollData = useDealersByCityWithInfiniteScroll({
    city: originalData.city || '',
    enableInfiniteScroll,
    pageSize: 20
  });
  
  // Determina quale dataset utilizzare
  const useInfiniteScroll = enableInfiniteScroll && !isSSR();
  const data = useInfiniteScroll ? infiniteScrollData : originalData;
  
  // Destructuring con gestione delle proprietà opzionali
  const {
    cityDealers,
    isLoading,
    filters,
    formattedCity,
    city,
    view,
    setView,
    updateFilters,
    handleCardClick,
    handleGoBack,
    handleRefresh,
    handleDealerSelect
  } = data;

  // Timer per nascondere loading iniziale dopo un breve periodo
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 800); // 800ms per garantire transizione smooth

    return () => clearTimeout(timer);
  }, []);

  // Calcola stato di loading finale - mostra sempre loading inizialmente
  const showLoading = initialLoading || isLoading;
  
  // Proprietà infinite scroll (disponibili solo se abilitato)
  const isLoadingMore = (data as any).isLoadingMore || false;
  const hasMore = (data as any).hasMore || false;
  const loadMore = (data as any).loadMore || (() => {});
  const totalCount = (data as any).totalCount || cityDealers?.length || 0;
  const isInfiniteScrollEnabled = (data as any).enableInfiniteScroll || false;
  
  const { createCitySlug } = useDealerUtils();
  const citySlug = city || '';

  // Fetch SEO stats for city-specific page
  const { stats } = useSEOStats({ 
    city: formattedCity, 
    enabled: !isSSR() && !!formattedCity 
  });

  // During client-side rendering, show loading or not found states
  if (!isSSR() && showLoading) {
    return (
      <>
        <MainNav />
        <div className="container mx-auto px-4 py-6 sm:px-6">
          <DealersCityHeader 
            formattedCity={formattedCity || "Caricamento..."} 
            dealersCount={0} 
            onGoBack={handleGoBack} 
          />
          <div className="bg-card rounded-lg shadow-sm p-4 mb-4 w-full">
            <Skeleton className="h-10 w-full max-w-[300px]" />
          </div>
          <div className="bg-card rounded-lg shadow-sm p-4 w-full">
            <div className="space-y-6 w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="space-y-3">
                    <Skeleton className="h-48 w-full rounded-lg" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Only render SEO component on client-side, server-side SEO is handled by ServerDealersCityContent */}
      {!isSSR() && (
        <DealersCitySEO 
          formattedCity={formattedCity} 
          citySlug={citySlug} 
          dealers={cityDealers} 
        />
      )}
      
      <MainNav />
      <div className="container mx-auto px-4 py-6 sm:px-6">
        <DealersCityHeader 
          formattedCity={formattedCity} 
          dealersCount={cityDealers.length} 
          onGoBack={handleGoBack} 
        />

        {isSSR() ? (
          <ServerDealersCityContent 
            dealers={cityDealers} 
            formattedCity={formattedCity} 
            citySlug={createCitySlug(formattedCity)}
            cityStats={stats}
          />
        ) : (
          <>
            <div className="bg-card rounded-lg shadow-sm p-4 mb-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                  <DealersFilter 
                    filters={filters}
                    onUpdateFilters={updateFilters}
                    onRefresh={handleRefresh}
                    isLoading={isLoading}
                  />
                  
                  <Tabs 
                    defaultValue="list" 
                    value={view} 
                    onValueChange={(v) => setView(v as "list" | "map")} 
                    className="w-full sm:w-auto"
                  >
                    <TabsList className="grid w-full sm:w-[200px] grid-cols-2">
                      <TabsTrigger value="list" className="flex items-center gap-2">
                        <List className="h-4 w-4" />
                        Lista
                      </TabsTrigger>
                      <TabsTrigger value="map" className="flex items-center gap-2">
                        <Map className="h-4 w-4" />
                        Mappa
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm p-4">
              {view === "map" ? (
                <DealersMap dealers={cityDealers} />
              ) : (
                <DealersListView 
                  dealers={cityDealers} 
                  isLoading={isLoading}
                  isLoadingMore={isLoadingMore}
                  hasMore={hasMore}
                  totalCount={totalCount}
                  onDealerClick={handleCardClick}
                  onRefresh={handleRefresh}
                  onLoadMore={loadMore}
                  enableInfiniteScroll={isInfiniteScrollEnabled}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default DealersByCity;
